import React from 'react';
import { VerifyBankBodyWrapper } from '../deposit/styled.jsx';
import Header from '../header/index.jsx';
import PropTypes from 'prop-types';
import { Button } from '../button/index.jsx';
import { useTheme } from '@emotion/react';
import { getPlaidNuveiLogo } from '../../utils/common.js';

const PRIVACY_NOTICE_URL = 'https://www.nuvei.com/legal/privacy-notice';

const TermOfUser = ({ setViewTC = null }) => {
  const theme = useTheme();
  return (
    <VerifyBankBodyWrapper>
      <div className='tcContent'>
        <Header logoUrl={getPlaidNuveiLogo(theme)}/>
        <div className='topSection'>
          <div className='contentSection'>
            <div id='echeck-terms-of-use-text' className='echeck-terms-of-use-class'>
              <h2 style={{ textAlign: 'center' }}> Nuvei US, LLC Terms of Use </h2>

              <strong>Effective:</strong> July 2024
              <br />
              <br />
              <strong>ACCEPTANCE OF TERMS</strong>
              <br />
              <br />
              <strong>Nuvei US, LLC</strong> (hereinafter &ldquo;Nuvei&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) provides the Services to you subject to the following Terms of Service, which may be updated by us from time to time as set forth below. These terms of use are a legally binding contract between you and Nuvei governing your use of our payment services, information retrieval services, and information verification services.
              <br />
              <br />
              <strong>PRIVACY POLICY</strong>
              <br />
              <br />
              This Privacy Policy governs how we collect, use and disclose Personal Information. It applies to the use of our website, the services we offer and agreement that incorporates this policy by reference. Privacy policy located at{' '}
              <a href={PRIVACY_NOTICE_URL} target='_blank' rel='noopener noreferrer'>
                https://www.nuvei.com/legal/privacy-notice.
              </a>
              <br />
              <br />
              <strong>INFORMATION WE COLLECT</strong>
              <br />
              We collect non-public and Personal Information (&quot;Personal Information&quot;). Personal Information is information that is not readily available from a public source.
              <br />
              <br />
              Personal Information means information that identifies, relates to, describes, is capable of being associated with, or could reasonably be linked, directly or indirectly, with a consumer or household. Personal Information does not include de-identified information where your identity cannot be ascertained from the data
              <br />
              <br />
              We obtain Personal Information about you from the following sources:
              <br />
              <ul>
                <li>
                  Information we receive from you for the purpose of executing a transaction.
                </li>
                <li>
                  Information we collect about you when visiting our website through the use of Cookies.
                </li>
                <li>
                  We may also obtain information about you from third parties such as credit bureaus and identity
                  verification services.
                </li>
              </ul>
              <br />
              The following table summarizes the major categories of Personal Information that we collect from or about you.
              Note that the table is not intended to be a precise or comprehensive list of every data point we collect or use,
              but rather a reasonably complete representation of this information to aid you in understanding our data
              practices and your correspondent rights:
              <br />
              <br />
              <div className='custom-table'>
                <table>
                  <thead>
                    <tr>
                      <th>Category of Personal Information</th>
                      <th>Examples of Personal Information</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Identity Data</td>
                      <td>
                        <ul>
                          <li>Name</li>
                          <li>Username</li>
                          <li>Date of birth</li>
                          <li>Contact Data</li>
                        </ul>
                      </td>
                    </tr>
                    <tr>
                      <td>Contact Data</td>
                      <td>
                        <ul>
                          <li>Address</li>
                          <li>Email address</li>
                          <li>Telephone numbers</li>
                        </ul>
                      </td>
                    </tr>
                    <tr>
                      <td>Financial Data</td>
                      <td>
                        <ul>
                          <li>Bank routing and account number</li>
                        </ul>
                      </td>
                    </tr>
                    <tr>
                      <td>Transaction Data</td>
                      <td>
                        <ul>
                          <li>Details about payments to and from you</li>
                          <li>Other details of any transactions you enter into using or accessing the Services</li>
                        </ul>
                      </td>
                    </tr>
                    <tr>
                      <td>Technical Data</td>
                      <td>
                        <ul>
                          <li>Internet protocol (IP) address</li>
                          <li>Browser type and version</li>
                          <li>Other technology or information stored on the devices you allow us access to when you use
                            the Services
                          </li>
                        </ul>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <br />
              <br />
              <strong>HOW WE USE THE PERSONAL INFORMATION WE COLLECT</strong>
              <br />
              We may use information you provide to us to troubleshoot problems; manage risk, or to detect, prevent, and/or
              remediate fraud or other potentially prohibited or illegal activities; detect, prevent or remediate violations
              of policies or applicable agreements; manage and protect our information technology infrastructure; provide
              marketing and advertising; perform creditworthiness and solvency checks, compare information for accuracy and
              verify it with third parties.
              <br />
              <br />
              We will use your Personal Information in the following circumstances:
              <br />
              <br />
              <ul>
                <li>Where it is necessary for our legitimate interests (or those of an authorized third party).</li>
                <li>Where we need to comply with a legal obligation.</li>
              </ul>
              <br />
              <br />
              <strong>INFORMATION WE DISCLOSE AND TO WHOM</strong>
              <br />
              We restrict access to non-public Personal Information to our employees, agents and subcontractors who need this
              information to provide products or services to you. We may share your Personal Information with our third-party
              service providers, agents, subcontractors, and other associated organizations, our group companies and
              affiliates in order to complete tasks, provide the Services to you on our behalf, and otherwise fulfil our
              obligations. We require third-party service providers, under written agreements, to respect the security of your
              Personal Information and to treat it in accordance with applicable law.
              <br />
              <br />
              We may share your Personal Information and other data you provide to us for the following purposes:
              <br />
              <br />
              <ul>
                <li>To verify your identity.</li>
                <li>To detect and prevent fraud, money laundering, terrorist financing, and other financial crimes and
                  illicit activities.
                </li>
                <li>To facilitate the provision of services.</li>
                <li>To improve our information, technology, and security systems including websites and mobile
                  applications.
                </li>
                <li>To comply with a legal request or to protect or enforce our rights, including to comply with an
                  applicable law or regulation, or in response to a request for information if we believe disclosure is in
                  accordance with any applicable law, regulation, or legal process, to protect against or prevent actual
                  or potential fraud, unauthorized transactions, claims, or other liability, and to protect the rights,
                  property, and safety of us or others.
                </li>
                <li>To evaluate, effectuate, or otherwise transfer your Personal Information as part of a proposed or actual
                  merger, restructuring, transfer, or exchange of all or a portion of our assets to another company.
                </li>
              </ul>
              <br />
              We may have disclosed the following categories of Personal Information to the following categories of third
              parties:
              <br />
              <br />
              <ul>
                <li>Companies and organizations that assist us in processing or verifying transactions.</li>
                <li>Fraud prevention service providers.</li>
                <li>Anyone to whom we lawfully transfer or may transfer our rights and duties under the relevant agreement
                  governing the use of any of the Services.
                </li>
                <li>Regulatory and law enforcement authorities, where the law allows or requires us to do so.</li>
              </ul>
              <br />
              <br />
              <strong>RESIDENTS OF CALIFORNIA AND VERMONT</strong>
              <br />
              To the extent applicable and for other to carry out our everyday business, for purposes of compliance with the Gramm-Leach-Bliley Act, 15 U.S.C. 6802 (&ldquo;GLBA&rdquo;), the California Financial Information Privacy Act (&ldquo;CFIPA&rdquo;), the California Consumer Privacy Act (&ldquo;CCPA&rdquo;) and the Vermont Consumer Protection Act, 8 V.S.A. 10202 (&ldquo;VCPA&rdquo;), we will not share non-public personally identifiable financial information with our affiliates or non-affiliates unless we receive your prior approval (&ldquo;OPT IN&rdquo;) allowing us share this non-public personally identifiable information with our affiliates or non-affiliates and, for purposes of the GLBA. When you use our Services, our authorized partners may collect categories of Personal Information about you and use this data for their own purposes. This activity may qualify as a &ldquo;sale&rdquo; under the CCPA. If CCPA applies to you, you can make choices to allow or prevent such uses. Depending on your choices, during the past twelve months, we may have shared information within the categories defined in the table above.
              <br />
              This Privacy Policy shall serve as your annual privacy notice as defined under the GLBA, CFIPA and VCPA, respectively.
              <br />
              <br />
              <strong>OUR SECURITY PROCEDURES</strong>
              <br />
              We use reasonable efforts not to disclose Personal Non-public Information to any third party except for the
              reasons set forth in below or with your consent. Credit bureaus, customer service agents, collection agencies,
              and other trusted third parties provide services to us in order to deliver our services to you, and such third
              parties are bound by this Privacy Policy.
              <br />
              <br />
              <strong>MARKETING</strong>
              <br />
              We do not sell or rent your Personal Information to third parties for their marketing purposes without your
              explicit consent. We may combine your Personal Information with information we collect from other companies and
              use it to improve and personalize our services, content, and advertising.
              <br />
              <br />
              <strong>COOKIES</strong>
              <br />
              Cookies are small data files which we (and the companies we work with) may place on your computer or other
              device. We use Cookies to recognize you as a customer; customize our services, content, and advertising; help
              ensure that your account security is not compromised; mitigate risk and prevent fraud.
              <br />
              You can decline accepting our Cookies if your browser or browser add-on permits, however, declining our Cookies
              may interfere with your use of our website and services.
              <br />
              <br />
              <strong>DO NOT TRACK</strong>
              <br />
              Do Not Track is an optional browser setting that allows you to express your preferences regarding tracking by
              advertisers and other third parties. We do not currently respond to Do Not Track signals and we provide you
              notice of the tracking by advertisers and other third parties in our Privacy and Cookies policies.
              <br />
              <br />
              <strong>YOUR LEGAL RIGHTS</strong>
              <br />
              Under certain circumstances, you have rights under applicable law in relation to your Personal Information:
              <br />
              <br />
              <ul>
                <li>Right to know about Personal Information collected or disclosed.</li>
                <li>Request correction of your Personal Information.</li>
                <li>Request erasure/deletion of your Personal Information.</li>
                <li>Right to Non-Discrimination for the Exercise of a Consumer&rsquo;s privacy Rights. You will not receive a
                  discriminatory treatment by us for the exercise of the privacy rights set out above.
                </li>
              </ul>
              <br />
              If you wish to exercise any of the rights set out above or if you want to raise a question or a concern about
              our privacy policy and practices, please contact us. We will promptly take steps to disclose and deliver to you,
              free of charge, your Personal Information according to your Verifiable Consumer Request to Know. We may also
              contact you to ask you for further information in relation to your request to speed up our response.
              <br />
              <br />
              <strong>GENERAL</strong>
              <br />
              We may change this Privacy Policy at any time by posting the changed terms to this page. Any changes take effect
              30 days from posting.
              <br />
              This Policy is governed by the laws of the state of Arizona, and you irrevocably submit to the exclusive
              jurisdiction of the courts of the state of Arizona.
              <br />
              <br />
              <strong>ACH AUTHORIZATION AND AGREEMENT</strong>
              <br />
              This ACH Authorization and Agreement (&ldquo;Agreement&rdquo;) is in connection to online payments sent to or received from the Merchant whose website you are visiting. Nuvei is acting on behalf of the Merchant for the purpose of collecting funds from you. By using your bank and routing account as a payment method, you accept and agree to this Agreement and acknowledge and agree that: you are the owner of the bank account associated with this transaction; you are duly authorized in accordance with your bank account agreement with your issuing bank to authorize debit from and credit to such bank accounts; further, that your bank account is/are capable of and allow inbound and outbound ACH transactions and is in good standing; you are authorized to make withdrawals from the identified account and credits to the same account without the approval or participation of anyone else; and you authorize Us on behalf of the Merchant to collect, verify and retain your name, username, bank account information in order to initiate credit or debit entries to or from your bank account to the Merchant from time to time and solely upon your request via the Service platform until no further credits or debits are authorized or permitted under this Agreement.
              <br />
              <br />
              When you use your bank routing and account numbers for any transaction, you are requesting an electronic transfer from your bank account. For these transactions, you agree and authorize Nuvei US, LLC to debit your bank account in the amount you specify from the account number and routing number previously provided. You understand that if your payment is returned unpaid you may be assessed a fee in accordance with applicable state law, therefore you authorize Nuvei US, LLC to debit and/or credit your account for refunds or to correct any errors, if necessary, via electronic funds transfer. If a payment is returned by your Bank, we, the merchant, or third party appointed by us or the Merchant, may contact you for payment via an alternate method
              using the information you have provided to the Merchant, to us, or to any of the Merchant&rsquo;s service providers to collect payment via an alternate method. You understand that this authorization shall remain in full force and effect until you notify Nuvei US, LLC by the method they specify that you wish to revoke your authorization prior to settlement, within two banking days by contacting the Customer Service Department at ibtsupport@nuvei.com. Please note that in some instances, and especially in utilization of the Real Time Payments (RTP) network, given the timing of the processing of transactions, you may not have the ability to revoke your authorization and the transaction will be considered complete upon submission. You also recognize that in the event that your bank does not support the processing of Real Time Payments via the RTP network, your transaction will default to an electronic funds transfer through the ACH network and will adhere to those processing timelines as outlined by Nacha.
              <br />
              <br />
              Nuvei US, LLC. may initiate a reversing file and/or an entry on behalf of the merchant to correct (reverse) all entries of an erroneous or duplicate file.
              <br />
              <br />
              You agree that such requests constitute your authorization and express consent to make the transfers to the Merchant and receive funds from the Merchant in the form of reversals or pay-outs.
              <br />
              <br />
              The origination of ACH transactions to and from your bank account is initiated by a request on your behalf and
              only upon the date of such request.
              <br />
              <br />
              Your ACH Agreement is a continuing one that will remain in full force and effect until you cancel this Agreement by notifying the Merchant directly via one of the Contact methods outlined on the Merchant&rsquo;s website. You agree to do so in time to allow the Merchant to act on your cancellation and you understand that you may only give notice of cancellation of this Agreement by contacting the Merchant, and such failure to contact Merchant will make your notice of cancellation or revocation of your authorization of certain ACH and RTP transactions null and void. You acknowledge and agree that you will be deemed to have signed this Agreement by and on the date, you accept to use this Service and by logging in to your bank account and allowing us to retain your information.
              <br />
              <br />
              You acknowledge and agree that any funds owed to you by the Merchant pursuant to your pre-existing relationship with the Merchant, and made available to you by us via our Service platform shall be considered the same as a payment made directly to you by the Merchant. You acknowledge that upon our receipt of transaction funds from the Merchant for the agreed-upon amount, the Merchant&rsquo;s obligation to you is extinguished and We are responsible for settling the funds at your bank account in accordance with this Agreement.
              <br />
              <br />
              You have the right to receive reimbursement for any debit that is not authorized by you or is inconsistent with this Agreement. To obtain more information on your recourse rights, contact your bank directly.
              <br />
              <br />
            </div>
          </div>
        </div>
        <Button type='default' onClick={() => setViewTC(false)}>
          Continue
        </Button>
      </div>
    </VerifyBankBodyWrapper>
  );
};

TermOfUser.propTypes = {
  /**
   * Back Deposit Screen when user click close
   */
  setViewTC: PropTypes.func,
};

export default TermOfUser;
